import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ArrowLeft, Sparkles, Eye, EyeOff, Shield } from 'lucide-react';
import SuccessAlert from './SuccessAlert';
import { useAuth } from '../context/AuthContext';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, validateOtpApi, resetPasswordApi } = useAuth();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    otp: ['', '', '', '', '', ''],
    token: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Calculate password strength for new password
    if (name === 'newPassword') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "bg-red-500";
      case 2:
        return "bg-yellow-500";
      case 3:
        return "bg-blue-500";
      case 4:
      case 5:
        return "bg-green-500";
      default:
        return "bg-gray-300";
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
      case 5:
        return "Strong";
      default:
        return "";
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...formData.otp];
    newOtp[index] = value;

    setFormData(prev => ({
      ...prev,
      otp: newOtp
    }));

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Clear OTP error
    if (errors.otp) {
      setErrors(prev => ({
        ...prev,
        otp: ''
      }));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const validateEmail = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};
    const otpString = formData.otp.join('');

    if (otpString.length !== 6) {
      newErrors.otp = 'Please enter the complete 6-digit code';
    } else if (!/^\d{6}$/.test(otpString)) {
      newErrors.otp = 'OTP must contain only numbers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (passwordStrength < 3) {
      newErrors.newPassword = 'Password is too weak. Include uppercase, lowercase, numbers, and special characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Send OTP
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    setIsLoading(true);
    try {
      const res = await forgotPassword({ email: formData.email });
      console.log('🔍 Forgot password API response:', res);
      console.log('🔍 Full response data:', JSON.stringify(res, null, 2));

      if (res.success && res.data) {
        // Extract token from different possible response structures
        let token = null;

        // Try different possible token locations in the response
        if (res.data.token) {
          token = res.data.token;
        } else if (res.data.data && res.data.data.token) {
          token = res.data.data.token;
        } else if (res.token) {
          token = res.token;
        }

        console.log('🔍 Extracted token:', token);
        console.log('🔍 Token type:', typeof token);
        console.log('🔍 Token length:', token ? token.length : 'null/undefined');

        if (!token) {
          console.error('❌ No token found in response');
          setErrors({ email: 'Server error: No verification token received' });
          return;
        }

        // Update form data with token
        setFormData(prev => {
          const updated = { ...prev, token: token };
          console.log('🔍 Updated formData with token:', updated.token);
          return updated;
        });

        setStep(2);
        startResendTimer();

        // Clear any previous errors
        setErrors({});
      } else {
        console.error('❌ Forgot password failed:', res.error);
        setErrors({ email: res.error?.message || 'Failed to send OTP' });
      }
    } catch (err) {
      console.error('❌ Forgot password error:', err);
      setErrors({ email: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Validate OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!validateOtp()) return;

    // Check if token exists before proceeding
    if (!formData.token) {
      console.error('❌ No token available for OTP validation');
      setErrors({ otp: 'Session expired. Please restart the process.' });
      setStep(1);
      return;
    }

    setIsLoading(true);
    try {
      const otpString = formData.otp.join('');
      const payload = {
        email: formData.email,
        otp: otpString,
        token: formData.token
      };

      console.log('🔍 Validate OTP payload:', payload);
      console.log('🔍 Current token in formData:', formData.token);
      console.log('🔍 Token being sent to API:', payload.token);

      const res = await validateOtpApi(payload);
      console.log('🔍 Validate OTP response:', res);
      console.log('🔍 Full OTP validation response:', JSON.stringify(res, null, 2));

      if (res.success) {
        // Check if a new token is provided in the response
        let newToken = formData.token; // Keep existing token as default

        if (res.data && res.data.token) {
          newToken = res.data.token;
          console.log('🔍 New token received from validate-otp:', newToken);
        } else {
          console.log('🔍 No new token in response, keeping existing token:', newToken);
        }

        // Update token if we got a new one
        setFormData(prev => {
          const updated = { ...prev, token: newToken };
          console.log('🔍 Updated formData after OTP validation:', updated.token);
          return updated;
        });

        setStep(3);
        setErrors({});
      } else {
        console.error('❌ OTP validation failed:', res.error);
        setErrors({ otp: res.error?.message || 'Invalid OTP. Please try again.' });
      }
    } catch (err) {
      console.error('❌ Validate OTP error:', err);
      setErrors({ otp: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset Password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    // Check if token exists before proceeding
    if (!formData.token) {
      console.error('❌ No token available for password reset');
      setErrors({ general: 'Session expired. Please restart the process.' });
      setStep(1);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        email: formData.email,
        token: formData.token,
        password: formData.newPassword
      };

      console.log('🔍 Reset password payload:', {
        email: payload.email,
        token: payload.token,
        password: '***hidden***'
      });

      const res = await resetPasswordApi(payload);
      console.log('🔍 Reset password response:', res);

      if (res.success) {
        setShowSuccessAlert(true);
        // Clear form data for security
        setFormData({
          email: '',
          otp: ['', '', '', '', '', ''],
          token: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => navigate('/signin'), 3000);
      } else {
        console.error('❌ Password reset failed:', res.error);
        setErrors({ general: res.error?.message || 'Failed to reset password' });
      }
    } catch (err) {
      console.error('❌ Reset password error:', err);
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const startResendTimer = () => {
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    try {
      const res = await forgotPassword({ email: formData.email });
      console.log('🔍 Resend OTP response:', res);

      if (res.success && res.data) {
        // Extract token from resend response
        let newToken = null;

        if (res.data.token) {
          newToken = res.data.token;
        } else if (res.data.data && res.data.data.token) {
          newToken = res.data.data.token;
        } else if (res.token) {
          newToken = res.token;
        }

        console.log('🔍 New token from resend:', newToken);

        if (newToken) {
          setFormData(prev => {
            const updated = { ...prev, token: newToken };
            console.log('🔍 Updated formData after resend:', updated.token);
            return updated;
          });
          startResendTimer();
          setErrors({});
        } else {
          console.error('❌ No token in resend response');
          setErrors({ otp: 'Failed to resend OTP. Please try again.' });
        }
      } else {
        console.error('❌ Resend OTP failed:', res.error);
        setErrors({ otp: res.error?.message || 'Failed to resend OTP' });
      }
    } catch (err) {
      console.error('❌ Resend OTP error:', err);
      setErrors({ otp: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[1, 2, 3].map((stepNumber) => (
          <React.Fragment key={stepNumber}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
              step >= stepNumber
                ? 'bg-teal-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {stepNumber}
            </div>
            {stepNumber < 3 && (
              <div className={`w-12 h-1 transition-all duration-300 ${
                step > stepNumber ? 'bg-teal-600' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Debug info (remove in production)
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV === 'production') return null;

    return (
      <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs">
        <strong>Debug Info:</strong>
        <div>Current Step: {step}</div>
        <div>Token: {formData.token ? `${formData.token.substring(0, 10)}...` : 'null'}</div>
        <div>Email: {formData.email}</div>
        <div>OTP: {formData.otp.join('')}</div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-200 via-orange-100 to-orange-50 flex items-center justify-center p-4">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-200 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-orange-300 rounded-full opacity-30 animate-float-delayed"></div>
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-teal-100 rounded-full opacity-25 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-orange-200 rounded-full opacity-20 animate-float-delayed"></div>
      </div>

      {/* Main Container */}
      <div className="relative w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-teal-800 mb-2">Reset Password</h1>
          <p className="text-gray-600">
            {step === 1 && "Enter your email to receive a reset code"}
            {step === 2 && "Enter the 6-digit code sent to your email"}
            {step === 3 && "Create your new password"}
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Form Container */}
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white border-opacity-50 p-8 transform hover:scale-[1.02] transition-all duration-300">

          {/* Step 1: Email Input */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Mail className="w-4 h-4 text-teal-600" />
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className={`w-full px-4 py-3 pl-12 bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 ${
                      errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  />
                  <Mail className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    errors.email ? 'text-red-400' : 'text-gray-400'
                  }`} />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm animate-fade-in">{errors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending Code...
                  </>
                ) : (
                  <>
                    Send Reset Code
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Input */}
          {step === 2 && (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-600">
                  We've sent a 6-digit code to
                </p>
                <p className="font-semibold text-teal-700">{formData.email}</p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 justify-center">
                  <Shield className="w-4 h-4 text-teal-600" />
                  Verification Code
                </label>
                <div className="flex gap-2 justify-center">
                  {formData.otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className={`w-12 h-12 text-center text-xl font-bold bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 ${
                        errors.otp ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <p className="text-red-500 text-sm text-center animate-fade-in">{errors.otp}</p>
                )}
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || isLoading}
                  className="text-sm text-teal-600 hover:text-teal-800 font-medium transition-colors duration-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm animate-shake">
                  {errors.general}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Lock className="w-4 h-4 text-teal-600" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter your new password"
                    className={`w-full px-4 py-3 pl-12 pr-12 bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 ${
                      errors.newPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  />
                  <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    errors.newPassword ? 'text-red-400' : 'text-gray-400'
                  }`} />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        />
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          passwordStrength >= 3
                            ? "text-green-600"
                            : passwordStrength >= 2
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                  </div>
                )}

                {errors.newPassword && (
                  <p className="text-red-500 text-sm animate-fade-in">{errors.newPassword}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Lock className="w-4 h-4 text-teal-600" />
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your new password"
                    className={`w-full px-4 py-3 pl-12 pr-12 bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 ${
                      errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  />
                  <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    errors.confirmPassword ? 'text-red-400' : 'text-gray-400'
                  }`} />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm animate-fade-in">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Debug Info */}
          {/* {renderDebugInfo()} */}

          {/* Navigation Links */}
          <div className="mt-8 text-center space-y-2">
            <Link
              to="/signin"
              className="block text-teal-600 hover:text-teal-800 font-semibold transition-colors duration-200 hover:underline"
            >
              Back to Sign In
            </Link>
            <Link
              to="/"
              className="block text-gray-600 hover:text-gray-800 text-sm transition-colors duration-200 hover:underline"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Success Alert */}
      <SuccessAlert
        isVisible={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        title="Password Reset Successful!"
        message="Your password has been reset successfully. You can now sign in with your new password."
        type="success"
      />

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
